import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Field, Form, Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import ReCAPTCHA from 'react-google-recaptcha';
import { toast } from 'react-toastify';

// service
import { useLoginMutation, useGooglesigninMutation } from 'src/api/auth.api';
import type { LoginRequest } from 'src/api/request_models/auth/LoginRequest';
import type { LoginErrorResponse } from 'src/api/response_models/auth/LoginResponse';

// constants
import { ROUTES } from 'src/constants/routes';

// assets
import logo from 'assets/logo.svg';
import GoogleIcon from 'assets/icons/google-icon.svg';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

// components
import Image from 'src/shared/Image';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Message from 'src/shared/Message';
import { useGoogleLogin } from '@react-oauth/google';
import { setProfilePictureKey, setUsername } from '../redux/slices/userReducer.slice';
import { setSuperUser } from '../redux/slices/authReducer.slice';
import useLocalizeDocumentAttributes from '../i18n/useLocalizeDocumentAttributes';
import { authReducer } from '../redux/slices/authReducer.slice';

// Lazy load images
const loginPageImage = () => import('assets/login-page-image.jpg');
const loginPageImageWebP = () => import('assets/login-page-image2.webp');
const fallbackImage = () => import('../../assets/fallback-image.png');

export const LoginScreen = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const recaptcha = useRef<ReCAPTCHA>(null);
  const dispatch = useDispatch();
  const [error, setError] = useState<string>();
  const [login, { isLoading }] = useLoginMutation();
  const [images, setImages] = useState({
    loginPage: '',
    loginPageWebP: '',
    fallback: '',
  });

  const initialValues: LoginRequest = {
    username: '',
    password: '',
  };
  const [showPassword, setShowPassword] = useState(false);

  const { t, i18n } = useTranslation();
  const rtl = useLocalizeDocumentAttributes();

  const activeLocale = i18n.resolvedLanguage;

  const uidb64 = searchParams.get('uidb64');
  const token = searchParams.get('token');

  if (uidb64 && token) {
    const decodedPath = `${ROUTES.RESET_PASSWORD_CONFIRMATION}?uidb64=${uidb64}&token=${token}`;
    navigate(decodedPath);
  }
  const [googleSigIn] = useGooglesigninMutation();

  // Load images when component mounts
  useEffect(() => {
    Promise.all([loginPageImage(), loginPageImageWebP(), fallbackImage()]).then(([jpg, webp, fallback]) => {
      setImages({
        loginPage: jpg.default,
        loginPageWebP: webp.default,
        fallback: fallback.default,
      });
    });
  }, []);

  const onSubmit = async (values: LoginRequest) => {
    if (!recaptcha.current?.getValue()) {
      setError('Help us keep the site secure by completing this reCAPTCHA.');
      return;
    }
    try {
      setError('');
      const { error, data } = await login({
        username: values.username?.trim(),
        password: values.password?.trim(),
      });

      if (error) {
        let { data } = error as LoginErrorResponse;
        setError(data.message as string);
        toast.error(data.message as string);
      } else {
        dispatch(authReducer.actions.login(data));
        dispatch(setUsername(values.username));
        dispatch(setProfilePictureKey(data?.user?.image_key ?? ''));

        // Get and decode the redirect path from URL query params
        const redirectTo = searchParams.get('to');
        let decodedPath = redirectTo ? decodeURIComponent(redirectTo) : ROUTES.MAP_OVERVIEW;

        if (data?.user?.is_superuser) {
          dispatch(setSuperUser(true));
          decodedPath = ROUTES.ADMIN;
        }

        toast.success(
          <Message
            notification={{
              title: 'Login successful',
              body: 'Welcome to Autilent',
            }}
          />,
        ); // Navigate to the decoded path
        navigate(decodedPath);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleGoogleLogin = async (codeResponse: any) => {
    const { error, data } = await googleSigIn({
      access_token: codeResponse.access_token,
    });

    if (error) {
      let { data } = error as LoginErrorResponse;
      setError(data.message as string);
      toast.error(data.message as string);
    } else {
      dispatch(authReducer.actions.login(data));
      dispatch(setUsername(data?.user?.username || ''));
      dispatch(setProfilePictureKey(data?.user?.image_key ?? ''));

      // Get and decode the redirect path from URL query params
      const redirectTo = searchParams.get('to');
      let decodedPath = redirectTo ? decodeURIComponent(redirectTo) : ROUTES.MAP_OVERVIEW;

      if (data?.user?.is_superuser) {
        dispatch(setSuperUser(true));
        decodedPath = ROUTES.ADMIN;
      }

      toast.success(
        <Message
          notification={{
            title: 'Login successful',
            body: 'Welcome to Autilent',
          }}
        />,
      ); // Navigate to the decoded path
      navigate(decodedPath);
    }
  };
  return (
    <div ref={rtl} className="flex flex-row h-dvh">
      <div className="flex flex-col flex-1 h-full justify-center">
        <div className="flex flex-col items-center px-5 md:px-32 h-full">
          <Link to={ROUTES.LOGIN} className="hidden md:flex">
            <div className="flex flex-col items-center">
              <img src={logo} alt="logo" className="mb-2 mt-24" />
              <h1 className="text-[2em] font-extrabold mb-2">{t('autilent')}</h1>
            </div>
          </Link>
          <p className="text-[16px] font-medium leading-[100%] tracking-[0%] text-center">
            Leveraging <span className="font-bold text-primary-blue">Artificial Intelligence</span> for Road Safety
          </p>

          <h1
            className="mt-10 text-[26px] font-bold leading-[100%] tracking-[0%] text-center font-['DM_Sans'] text-dark-grey
          "
          >
            Log in to your account
          </h1>
          <Formik
            initialValues={initialValues}
            validationSchema={Yup.object({
              username: Yup.string().required('Username is required'),
              password: Yup.string().required('Password is required'),
            })}
            onSubmit={onSubmit}
          >
            <Form className="mt-9 md:px-10 lg:max-w-[25vw] w-full">
              <label className="block " htmlFor="username">
                <span className="text-[14px] font-bold text-sm text-grey-mute">{t('username')}</span>
                <Field
                  name="username"
                  className="input rounded-lg focus:outline-1 focus:outline-offset-0 focus:outline-primary-blue placeholder:text-sm placeholder:text-muted font-normal border-muted w-full h-12 mt-1"
                  placeholder={t('enter_your_username')}
                />
                <ErrorMessage name="username" component="p" className="text-red-500 text-xs mt-1" />
              </label>
              <label className="block mt-4">
                <div className="justify-between flex">
                  <span className="text-[14px] font-bold text-sm text-grey-mute">{t('password')}</span>
                  <span className="text-[14px] font-bold text-sm text-grey-mute">
                    <Link
                      to={ROUTES.RESET_PASSWORD}
                      className="block text-right font-normal text-muted hover:text-primary-blue"
                    >
                      {t('forgot_password')}
                    </Link>
                  </span>
                </div>

                <div className="relative w-full">
                  <Field
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    className="input rounded-lg focus:outline-1 focus:outline-offset-0 focus:outline-primary-blue placeholder:text-sm placeholder:text-muted font-normal border-muted w-full h-12 mt-1"
                    placeholder={t('enter_your_password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute inset-y-0 ${activeLocale == 'en' ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center justify-center  pt-2`}
                  >
                    {showPassword ? (
                      <EyeIcon className="w-5 h-5 text-deep-blue" />
                    ) : (
                      <EyeSlashIcon className="w-5 h-5 text-deep-blue" />
                    )}
                  </button>
                </div>
                <ErrorMessage name="password" component="p" className="text-red-500 text-xs mt-1" />
              </label>
              {error && <p className="text-red-500 login-error-message">{error}</p>}{' '}
              {/* error message if error state exists */}
              <div className="w-full justify-center flex">
                <ReCAPTCHA className="mt-6" sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY} ref={recaptcha} />
              </div>
              <div className="flex justify-center">
                <button
                  className="btn btn-outline border-stone-300 hover:bg-primary-blue hover:text-white bg-primary-blue text-white text-base font-bold mt-6 h-12 px-8 w-full"
                  disabled={isLoading}
                >
                  {isLoading && <span className="loading loading-spinner"></span>} {t('log_in')}
                </button>
              </div>
              <div className="mt-6 w-full flex justify-center items-center ">
                <hr className="w-full" />
                <span className="text-grey-mute px-2"> OR </span>
                <hr className="w-full" />
              </div>
              <GoogleLoginButton handleSubmit={payload => handleGoogleLogin(payload)} />
              {/*<p className="mt-36">Dont have an account? <Link to="#" className="font-bold">Sign Up</Link></p>*/}
            </Form>
          </Formik>
        </div>
      </div>
      <div className="hidden md:flex-1 md:block h-full">
        {images.loginPage && (
          <Image
            webpSrc={images.loginPageWebP}
            src={images.loginPage}
            errorFallbackSrc={images.fallback}
            className="object-cover object-center h-full w-full"
            alt="login page"
            lazy={true}
          />
        )}
      </div>
    </div>
  );
};

function GoogleLoginButton({ handleSubmit }: { handleSubmit: (payload: any) => void }) {
  const login = useGoogleLogin({
    // flow: 'auth-code',
    flow: 'implicit',
    onSuccess: async codeResponse => {
      handleSubmit(codeResponse);
    },
    onError: error => {
      toast.error(error as string);
    },
  });

  return (
    <button
      onClick={() => login()}
      className="w-full h-12 mt-10 px-8 text-base font-normal text-black bg-white border border-stone-300 rounded-lg hover:bg-gray-100 transition-colors"
    >
      <div>
        <img src={GoogleIcon} alt="Google" className="w-5 h-5 mr-2 inline-block" />
        Continue with Google
      </div>
    </button>
  );
}

export default GoogleLoginButton;
